// GET /api/integrations/status - the one aggregate behind every
// connection surface. No fixture fallback: an unreachable server throws
// and the rows stay "Not verified".

import { getAuthHeaders } from "./auth";

export type ProviderState = "connected" | "configured" | "action_required" | "not_configured";

export interface ProviderStatus {
  id: string;
  state: ProviderState;
  detail?: string;
  lastSyncAt?: string;
  /** The Google account behind a connected OAuth row. */
  account?: string;
  /** What this surface accesses when live - the server's scope
      constants, state-independent. */
  scopes?: string[];
}

export async function fetchIntegrationsStatus(): Promise<ProviderStatus[]> {
  const res = await fetch("/api/integrations/status", {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`Integrations status failed: ${res.status}`);
  const payload = (await res.json()) as { providers?: ProviderStatus[] };
  return payload.providers ?? [];
}

// Admin-only Stripe key management. The server answers presence and
// provenance only - a stored key never comes back over the wire, so the
// UI is replace-only by construction.

export interface StripeConfigStatus {
  configured: boolean;
  source: "db" | "env" | "none";
  mode: "live" | "test" | null;
  webhookSecretSet: boolean;
  unreadable: boolean;
  canStore: boolean;
  updatedAt: string | null;
  updatedByEmail: string | null;
  /** POST only - a mode swap with checkout sessions still pending. */
  warning?: string;
}

/** Shared by every admin credential block: the server's own text is the
    actionable part, so surface it over a bare status code. */
async function integrationJson<T>(res: Response, fallback: string): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? `${fallback}: ${res.status}`);
  }
  return (await res.json()) as T;
}

function stripeConfigResponse(res: Response): Promise<StripeConfigStatus> {
  return integrationJson<StripeConfigStatus>(res, "Stripe settings request failed");
}

export async function fetchStripeConfig(): Promise<StripeConfigStatus> {
  const res = await fetch("/api/integrations/stripe", {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  return stripeConfigResponse(res);
}

export async function saveStripeKeys(body: {
  secretKey?: string;
  webhookSecret?: string;
}): Promise<StripeConfigStatus> {
  const res = await fetch("/api/integrations/stripe", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    credentials: "same-origin",
    body: JSON.stringify(body),
  });
  return stripeConfigResponse(res);
}

export async function removeStripeKeys(): Promise<StripeConfigStatus> {
  const res = await fetch("/api/integrations/stripe", {
    method: "DELETE",
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  return stripeConfigResponse(res);
}

/** Live identity check for the resolved key - facts only, never the key. */
export interface StripeAccountProbe {
  ok: boolean;
  accountId: string | null;
  chargesEnabled: boolean | null;
  errorCode: string | null;
}

export async function testStripeConnection(): Promise<StripeAccountProbe> {
  const res = await fetch("/api/integrations/stripe/test", {
    method: "POST",
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  return integrationJson<StripeAccountProbe>(res, "Stripe connection test failed");
}

export interface StripeWebhookRegistration extends StripeConfigStatus {
  webhookUrl: string;
  endpointId: string;
  replaced: boolean;
}

export async function registerStripeWebhook(): Promise<StripeWebhookRegistration> {
  const res = await fetch("/api/integrations/stripe/webhook", {
    method: "POST",
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  return integrationJson<StripeWebhookRegistration>(res, "Webhook registration failed");
}

// Admin-only Anthropic key management. Same contract as the Stripe block:
// presence and provenance out, the key itself never comes back.

export interface AnthropicConfigStatus {
  configured: boolean;
  source: "db" | "env" | "none";
  unreadable: boolean;
  canStore: boolean;
  updatedAt: string | null;
  updatedByEmail: string | null;
}

function anthropicConfigResponse(res: Response): Promise<AnthropicConfigStatus> {
  return integrationJson<AnthropicConfigStatus>(res, "Anthropic settings request failed");
}

export async function fetchAnthropicConfig(): Promise<AnthropicConfigStatus> {
  const res = await fetch("/api/integrations/anthropic", {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  return anthropicConfigResponse(res);
}

export async function saveAnthropicKey(apiKey: string): Promise<AnthropicConfigStatus> {
  const res = await fetch("/api/integrations/anthropic", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    credentials: "same-origin",
    body: JSON.stringify({ apiKey }),
  });
  return anthropicConfigResponse(res);
}

export async function removeAnthropicKey(): Promise<AnthropicConfigStatus & { removed: boolean }> {
  const res = await fetch("/api/integrations/anthropic", {
    method: "DELETE",
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  return integrationJson<AnthropicConfigStatus & { removed: boolean }>(
    res,
    "Anthropic settings request failed",
  );
}

/** Liveness check for the resolved key - a verdict, never the key. */
export interface AnthropicKeyProbe {
  ok: boolean;
  errorCode: string | null;
  /** The API's own words on a refusal - a status code alone cannot tell
      an admin whether to fix the key or the billing. */
  reason: string | null;
}

export async function testAnthropicKey(): Promise<AnthropicKeyProbe> {
  const res = await fetch("/api/integrations/anthropic/test", {
    method: "POST",
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  return integrationJson<AnthropicKeyProbe>(res, "Anthropic connection test failed");
}

// Admin-only: which model answers for each AI role. Two reads, because the
// choice is stored locally and always available while the catalogue is a
// live call to Anthropic that can refuse.

export interface AiModelChoices {
  choices: Record<string, string>;
  defaults: Record<string, string>;
  updatedAt: string | null;
  updatedByEmail: string | null;
}

export interface AiCatalogueModel {
  id: string;
  displayName: string;
  maxOutputTokens: number | null;
}

export interface AiModelCatalogue {
  models: AiCatalogueModel[];
  errorCode: string | null;
  reason: string | null;
}

export async function fetchAiModels(): Promise<AiModelChoices> {
  const res = await fetch("/api/integrations/ai/models", {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  return integrationJson<AiModelChoices>(res, "AI model settings request failed");
}

export async function fetchAiModelCatalogue(): Promise<AiModelCatalogue> {
  const res = await fetch("/api/integrations/ai/models/catalogue", {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  return integrationJson<AiModelCatalogue>(res, "Model list request failed");
}

export async function saveAiModels(models: Record<string, string>): Promise<AiModelChoices> {
  const res = await fetch("/api/integrations/ai/models", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    credentials: "same-origin",
    body: JSON.stringify({ models }),
  });
  return integrationJson<AiModelChoices>(res, "AI model settings request failed");
}

// Admin-only Google Workspace ids. Not credentials - WHERE the firm's
// service account works - so these come back in full, and the form edits
// them rather than replacing an invisible value.

/** Whether the service account is inside the Shared Drive, and as what -
    the server's last live check, or the one just asked for. */
export interface DriveMembershipProbe {
  ok: boolean;
  driveId: string;
  accountEmail: string | null;
  driveName: string | null;
  member: boolean;
  role: string | null;
  /** Google's own word for the role: Manager, Content manager, ... */
  roleLabel: string | null;
  errorCode: string | null;
  reason: string | null;
  checkedAt: string;
}

export interface GoogleWorkspaceConfigStatus {
  source: "db" | "env" | "none";
  sharedDriveId: string | null;
  excelMasterFileId: string | null;
  /** The address to invite into the Shared Drive; null while no
      service-account key is set on the server. */
  serviceAccountEmail: string | null;
  updatedAt: string | null;
  updatedByEmail: string | null;
  /** The last membership verdict about THIS drive id; null until checked. */
  membership: DriveMembershipProbe | null;
}

function googleWorkspaceResponse(res: Response): Promise<GoogleWorkspaceConfigStatus> {
  return integrationJson<GoogleWorkspaceConfigStatus>(res, "Workspace settings request failed");
}

export async function probeDriveMembership(): Promise<DriveMembershipProbe> {
  const res = await fetch("/api/integrations/google-workspace/probe", {
    method: "POST",
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  return integrationJson<DriveMembershipProbe>(res, "Drive access check failed");
}

export async function fetchGoogleWorkspaceConfig(): Promise<GoogleWorkspaceConfigStatus> {
  const res = await fetch("/api/integrations/google-workspace", {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  return googleWorkspaceResponse(res);
}

export async function saveGoogleWorkspaceConfig(body: {
  sharedDriveId: string;
  excelMasterFileId: string;
}): Promise<GoogleWorkspaceConfigStatus> {
  const res = await fetch("/api/integrations/google-workspace", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    credentials: "same-origin",
    body: JSON.stringify(body),
  });
  return googleWorkspaceResponse(res);
}

export async function removeGoogleWorkspaceConfig(): Promise<
  GoogleWorkspaceConfigStatus & { removed: boolean }
> {
  const res = await fetch("/api/integrations/google-workspace", {
    method: "DELETE",
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  return integrationJson<GoogleWorkspaceConfigStatus & { removed: boolean }>(
    res,
    "Workspace settings request failed",
  );
}

// Admin-only: who the firm's mail comes from. The SMTP password goes up
// and never comes back; every other field is an address the form edits.

export type MailTransport = "gmail-oauth" | "smtp";

export type StaffMfaPolicy = "off" | "optional" | "required-for-password-accounts";

export interface MailConfigStatus {
  source: "db" | "default";
  transport: MailTransport;
  fromAddress: string | null;
  fromName: string | null;
  replyTo: string | null;
  host: string | null;
  port: number | null;
  secure: boolean;
  user: string | null;
  passwordSet: boolean;
  unreadable: boolean;
  /** Fields this row still needs before a send can be attempted. */
  missing: string[];
  /** Written by a delivery that actually happened - the only evidence
      the mailbox works, and what the later capability gates read. */
  lastTestOkAt: string | null;
  lastErrorAt: string | null;
  lastError: string | null;
  updatedAt: string | null;
  updatedByEmail: string | null;
  canStore: boolean;
  /** m-4 policy: password sign-in waits for a confirmed mailbox. */
  requireVerifiedEmail: boolean;
  /** Who owes a second code at the password door. */
  mfaPolicy: StaffMfaPolicy;
  /** Whether the mailed code keeps standing in after an app is connected. */
  mfaEmailAfterApp: boolean;
}

export interface MailSettingsUpdate {
  transport?: MailTransport;
  requireVerifiedEmail?: boolean;
  mfaPolicy?: StaffMfaPolicy;
  mfaEmailAfterApp?: boolean;
  fromAddress?: string;
  fromName?: string;
  replyTo?: string;
  host?: string;
  port?: number;
  secure?: boolean;
  user?: string;
  password?: string;
}

export interface MailTestResult extends MailConfigStatus {
  ok: boolean;
  error: string | null;
}

function mailResponse(res: Response): Promise<MailConfigStatus> {
  return integrationJson<MailConfigStatus>(res, "Mail settings request failed");
}

export async function fetchMailConfig(): Promise<MailConfigStatus> {
  const res = await fetch("/api/integrations/mail", {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  return mailResponse(res);
}

export async function saveMailConfig(body: MailSettingsUpdate): Promise<MailConfigStatus> {
  const res = await fetch("/api/integrations/mail", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    credentials: "same-origin",
    body: JSON.stringify(body),
  });
  return mailResponse(res);
}

export async function removeMailConfig(): Promise<MailConfigStatus & { removed: boolean }> {
  const res = await fetch("/api/integrations/mail", {
    method: "DELETE",
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  return integrationJson<MailConfigStatus & { removed: boolean }>(
    res,
    "Mail settings request failed",
  );
}

/** Sends a real message to the signed-in admin. The recipient is not ours
    to choose - the server takes it from the session. */
export async function testMailConfig(): Promise<MailTestResult> {
  const res = await fetch("/api/integrations/mail/test", {
    method: "POST",
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  return integrationJson<MailTestResult>(res, "Test message failed");
}

// Admin-only: the client-portal sign-in policy. No secrets here - three
// switches and their provenance.

export type PortalOtpSecondFactor = "off" | "required";

export interface PortalPolicyStatus {
  /** Whether a password sign-in owes a mailed code afterwards. */
  otpSecondFactor: PortalOtpSecondFactor;
  /** Whether a mailed code alone may sign a client in, no password. */
  otpLoginEnabled: boolean;
  /** Whether the case ID and date of birth pair opens the portal. */
  caseIdLoginEnabled: boolean;
  source: "db" | "default";
  updatedAt: string | null;
  updatedByEmail: string | null;
}

export interface PortalPolicyUpdate {
  otpSecondFactor?: PortalOtpSecondFactor;
  otpLoginEnabled?: boolean;
  caseIdLoginEnabled?: boolean;
}

function portalResponse(res: Response): Promise<PortalPolicyStatus> {
  return integrationJson<PortalPolicyStatus>(res, "Portal settings request failed");
}

export async function fetchPortalPolicy(): Promise<PortalPolicyStatus> {
  const res = await fetch("/api/integrations/portal", {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  return portalResponse(res);
}

export async function savePortalPolicy(body: PortalPolicyUpdate): Promise<PortalPolicyStatus> {
  const res = await fetch("/api/integrations/portal", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    credentials: "same-origin",
    body: JSON.stringify(body),
  });
  return portalResponse(res);
}

export async function removePortalPolicy(): Promise<PortalPolicyStatus & { removed: boolean }> {
  const res = await fetch("/api/integrations/portal", {
    method: "DELETE",
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  return integrationJson<PortalPolicyStatus & { removed: boolean }>(
    res,
    "Portal settings request failed",
  );
}
